import { useEffect, useRef, useMemo } from "react";
import { Marker } from "@react-google-maps/api";
import { CurrentViewTypeEnum, PointsBaseMap } from "./typings";
import { MarkersContainer } from "./styles";
import { GeometryType } from "./typings";
import useModal from "../../../hooks/useRelatorioContext";

interface BaseMapProps {
  mapRef: any;
  displayMarkers: boolean;
  currentViewType: CurrentViewTypeEnum;
  points?: PointsBaseMap;
}

interface LatLngLiteral {
  lat: number;
  lng: number;
}

interface TooltipContent {
  codigo: string;
  operacoesCount: number;
  aderencia: number;
}

interface MapTooltip {
  show(position: LatLngLiteral, content: TooltipContent): void;
  hide(): void;
  refresh(): void;
  setMap(map: google.maps.Map | null): void;
}

function createMapTooltip(): MapTooltip {
  class TalhaoMapTooltip extends google.maps.OverlayView {
    private container: HTMLDivElement | null = null;
    private position: LatLngLiteral | null = null;
    private visible = false;

    onAdd() {
      this.container = document.createElement("div");
      Object.assign(this.container.style, {
        position: "absolute",
        background: "rgba(0, 0, 0, 0.75)",
        color: "#fff",
        padding: "6px 8px",
        borderRadius: "2px",
        fontSize: "14px",
        lineHeight: "1.5",
        pointerEvents: "none",
        boxShadow:
          "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08)",
        transform: "translate(-50%, calc(-100% - 10px))",
        display: "none",
        zIndex: "1000",
      });
      this.getPanes()?.floatPane.appendChild(this.container);
    }

    draw() {
      if (!this.container || !this.position || !this.visible) {
        if (this.container) this.container.style.display = "none";
        return;
      }

      const projection = this.getProjection();
      const point = projection?.fromLatLngToDivPixel(
        new google.maps.LatLng(this.position.lat, this.position.lng)
      );

      if (!point) return;

      this.container.style.left = `${point.x}px`;
      this.container.style.top = `${point.y}px`;
      this.container.style.display = "block";
    }

    show(position: LatLngLiteral, content: TooltipContent) {
      this.position = position;
      this.visible = true;

      if (this.container) {
        this.container.replaceChildren();

        const talhaoLine = document.createElement("div");
        talhaoLine.textContent = `Talhão T-${content.codigo}`;

        const operacoesLine = document.createElement("div");
        operacoesLine.textContent = `Operações: ${content.operacoesCount}`;

        const aderenciaLine = document.createElement("div");
        aderenciaLine.textContent = `${content.aderencia.toFixed(2)}% de aderência`;

        if (content.operacoesCount === 0) {
          operacoesLine.textContent = "Sem planejamento vinculado";
        }

        this.container.append(talhaoLine, operacoesLine, aderenciaLine);
      }

      this.draw();
    }

    hide() {
      this.visible = false;
      if (this.container) this.container.style.display = "none";
    }

    refresh() {
      this.draw();
    }

    onRemove() {
      this.container?.remove();
      this.container = null;
    }
  }

  return new TalhaoMapTooltip();
}

function BaseMap({
  mapRef,
  displayMarkers,
  currentViewType,
  points,
}: BaseMapProps) {
  const { setIsOpen, setDetails } = useModal();
  const hoveredTalhaoIdRef = useRef<string | null>(null);
  const pendingMouseoutRef = useRef<number | null>(null);
  const tooltipRef = useRef<MapTooltip | null>(null);

  const Polygons = useMemo(
    () =>
      points?.features.filter(
        (feature) =>
          feature.geometry.type === GeometryType.Polygon ||
          feature.geometry.type === GeometryType.MultiPolygon
      ) || [],
    [points]
  );

  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;

    map.data.forEach((f: any) => map.data.remove(f));
    map.data.addGeoJson(points as any);

    map.data.setStyle((feature: any) => {
      const stroke = feature.getProperty("stroke");
      const strokeWidth = 2;
      const strokeOpacity = Number(feature.getProperty("strokeOpacity")) || 1;
      const fill = feature.getProperty("fill");
      const fillOpacity = Number(feature.getProperty("fillOpacity"));

      if (!fill) {
        return {
          strokeColor: stroke || "#000",
          strokeWeight: strokeWidth,
          strokeOpacity,
          fillOpacity: 0,
        };
      }

      return {
        strokeColor: stroke || "#000",
        strokeWeight: strokeWidth,
        strokeOpacity,
        fillColor: fill,
        fillOpacity: isNaN(fillOpacity) ? 0.6 : fillOpacity,
      };
    });
  }, [mapRef, points]);

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const previousDisableDoubleClickZoom =
      map.get("disableDoubleClickZoom") ?? false;

    map.setOptions({ disableDoubleClickZoom: true });

    return () => {
      map.setOptions({
        disableDoubleClickZoom: previousDisableDoubleClickZoom,
      });
    };
  }, [mapRef]);

  useEffect(() => {
    if (!mapRef.current || typeof google === "undefined") return;

    const map = mapRef.current;
    const mapDiv = map.getDiv();

    const tooltip = createMapTooltip();
    tooltip.setMap(map);
    tooltipRef.current = tooltip;

    const cancelPendingMouseout = () => {
      if (pendingMouseoutRef.current !== null) {
        cancelAnimationFrame(pendingMouseoutRef.current);
        pendingMouseoutRef.current = null;
      }
    };

    const hideTooltip = () => {
      hoveredTalhaoIdRef.current = null;
      tooltip.hide();
    };

    const mouseoverListener = map.data.addListener(
      "mouseover",
      (event: google.maps.Data.MouseEvent) => {
        const id = event.feature.getProperty("id");
        const codigo = event.feature.getProperty("codigo");
        const operacoes =
          event.feature.getProperty("operacoes") ??
          event.feature.getProperty("planosoperacionais");
        const pctAderencia =
          event.feature.getProperty("PlanosOperacionaisPorcentagemAderente") ??
          event.feature.getProperty("planosOperacionaisPorcentagemAderente") ?? 0;
        const lat = event.feature.getProperty("pontoCentralLat");
        const lng = event.feature.getProperty("pontoCentralLong");

        if (!id || lat == null || lng == null) return;

        cancelPendingMouseout();
        hoveredTalhaoIdRef.current = id;
        tooltip.show(
          { lat, lng },
          {
            codigo,
            operacoesCount: Array.isArray(operacoes) ? operacoes.length : 0,
            aderencia: pctAderencia
          }
        );
      }
    );

    const mouseoutListener = map.data.addListener(
      "mouseout",
      (event: google.maps.Data.MouseEvent) => {
        const id = event.feature.getProperty("id");

        cancelPendingMouseout();
        pendingMouseoutRef.current = requestAnimationFrame(() => {
          pendingMouseoutRef.current = null;
          if (hoveredTalhaoIdRef.current === id) {
            hideTooltip();
          }
        });
      }
    );

    const boundsListener = map.addListener("bounds_changed", () => {
      tooltip.refresh();
    });

    const dblClickListener = map.data.addListener(
      "dblclick",
      (event: google.maps.Data.MouseEvent) => {
        event.stop();
        event.domEvent?.preventDefault();
        event.domEvent?.stopPropagation();
        const feature = event.feature;
        const talhaoId = feature.getProperty("id");
        const code = feature.getProperty("codigo");
        if (!talhaoId || !event.latLng) return;

        setIsOpen(true);
        setDetails({ talhaoId, code });
      }
    );

    const handleMapLeave = () => {
      cancelPendingMouseout();
      hideTooltip();
    };

    mapDiv.addEventListener("mouseleave", handleMapLeave);

    return () => {
      cancelPendingMouseout();
      google.maps.event.removeListener(mouseoverListener);
      google.maps.event.removeListener(mouseoutListener);
      google.maps.event.removeListener(boundsListener);
      google.maps.event.removeListener(dblClickListener);
      mapDiv.removeEventListener("mouseleave", handleMapLeave);
      tooltip.setMap(null);
      tooltipRef.current = null;
    };
  }, [mapRef, points]);

  return (
    <>
      <MarkersContainer style={{ pointerEvents: "none" }}>
        {Polygons.map((item, index) => (
          <Marker
            key={item.properties?.id || `marker-${index}`}
            clickable={false}
            visible={
              currentViewType !== CurrentViewTypeEnum.transparent &&
              displayMarkers
            }
            label={{
              text: item.properties?.codigo,
              color:
                currentViewType === CurrentViewTypeEnum.box ? "#000" : "#fff",
              fontWeight:
                currentViewType === CurrentViewTypeEnum.box
                  ? "regular"
                  : "bold",
            }}
            icon={{
              url: "/marker.png",
              size:
                currentViewType !== CurrentViewTypeEnum.box
                  ? new google.maps.Size(0, 0)
                  : undefined,
            }}
            position={{
              lat: item.properties?.pontoCentralLat,
              lng: item.properties?.pontoCentralLong,
            }}
          />
        ))}
      </MarkersContainer>
    </>
  );
}

export default BaseMap;
